import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummaryOwnProps as AccordionSummaryProps,
  IconProps,
  AccordionSummary as MuiAccordionSummary,
  Typography,
  accordionClasses,
  accordionDetailsClasses,
  accordionSummaryClasses,
} from "@mui/material";
import { Box, styled } from "@mui/system";
import { faChevronDown } from "@outschool/icons";
import React from "react";

import Icon from "../Icon/Icon";
import { withDisplayName } from "../../utils/displayName";

export {
  /**
   * @deprecated This component is deprecated. Please use OsAccordion instead.
   */
  Accordion,
  /**
   * @deprecated This component is deprecated. Please use OsAccordion instead.
   */
  accordionClasses,
  /**
   * @deprecated This component is deprecated. Please use OsAccordion instead.
   */
  AccordionDetails,
  /**
   * @deprecated Deprecated. Please use OsAccordionProps instead.
   */
  AccordionProps,
  /**
   * @deprecated Deprecated. Please use OsAccordionProps instead.
   */
  accordionSummaryClasses,
} from "@mui/material";

const ExpandIcon = () => (
  <Icon icon={faChevronDown} sx={{ color: "primary.800" }} />
);

export enum AccordionCornerRadius {
  small = "small",
  medium = "medium",
}

export enum AccordionColorScheme {
  neutral = "neutral", // white background, primary icons, black text
  primary = "primary", // primary background, primary icons, black text
  error = "error", // white background, error icons, black text
}

export enum AccordionSize {
  default = "default",
  compact = "compact",
}

const ACCORDION_BORDER_WIDTH = "2px";

const COLOR_SCHEME_MAP = {
  [AccordionColorScheme.neutral]: {
    backgroundColor: "common.white",
    iconColor: "primary.main",
    textColor: "common.black",
    borderColor: "neutral.light",
  },
  [AccordionColorScheme.primary]: {
    backgroundColor: "primary.50",
    iconColor: "primary.main",
    textColor: "common.black",
    borderColor: "neutral.light",
  },
  [AccordionColorScheme.error]: {
    backgroundColor: "common.white",
    iconColor: "error.main",
    textColor: "common.black",
    borderColor: "error.main",
  },
};

export interface OsAccordionProps extends Pick<AccordionProps, "children"> {
  /** The title text displayed in the accordion summary header */
  summaryTitle: string;
  /** Optional subtitle text displayed below the summary title */
  summarySubtitle?: string;
  /** The border radius size of the accordion. Defaults to 'small' (0.8rem) */
  cornerRadius?: AccordionCornerRadius;
  /** Optional icon displayed at the start of the summary title */
  startIcon?: React.ReactElement<IconProps>;
  /** If true, the accordion will be expanded by default on initial render */
  defaultExpanded?: boolean;
  /** If provided, the accordion expansion state will be controlled externally */
  expanded?: boolean;
  /** Callback fired when the accordion expansion state changes */
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
  /** The color scheme of the accordion. Defaults to 'neutral' (white background). Use 'error' for error states with red border and icons. */
  colorScheme?: AccordionColorScheme;
  /** The size of the accordion. Defaults to 'default' for general use. Use 'compact' for forms and dense layouts. */
  size?: AccordionSize;
}

export const OsAccordion = ({
  defaultExpanded,
  expanded,
  onChange,
  summarySubtitle,
  summaryTitle,
  cornerRadius = AccordionCornerRadius.small,
  colorScheme = AccordionColorScheme.neutral,
  size = AccordionSize.default,
  startIcon,
  children,
}: OsAccordionProps) => {
  const accordionId = React.useId();
  const contentId = `${accordionId}-content`;

  // Spacing values using 4px grid system
  const summaryVerticalPadding = size === AccordionSize.compact ? 0 : 12;
  const summaryHorizontalPadding = size === AccordionSize.compact ? 16 : 32;
  const detailsBottomPadding = size === AccordionSize.compact ? 12 : 24;

  const colors = COLOR_SCHEME_MAP[colorScheme];

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onChange={onChange}
      id={accordionId}
      sx={{
        width: "100%",
        backgroundColor: colors.backgroundColor,
        borderRadius:
          cornerRadius === AccordionCornerRadius.small
            ? "0.8rem !important"
            : "1.6rem !important",
        border: `${ACCORDION_BORDER_WIDTH} solid`,
        borderColor: colors.borderColor,
        // Remove Material-UI's default top border line between accordions
        "::before": {
          opacity: 0,
        },
        [`&.${accordionClasses.expanded}`]: {
          margin: 0,
          "::before": {
            opacity: 0,
          },
          "& + &": {
            "&::before": {
              display: "unset",
              opacity: 0,
            },
          },
        },
      }}
    >
      <MuiAccordionSummary
        expandIcon={
          <Icon
            icon={faChevronDown}
            sx={{
              alignSelf: "center",
              justifySelf: "center",
              color: colors.iconColor,
            }}
          />
        }
        aria-controls={contentId}
        sx={{
          color: colors.iconColor,
          padding: `${summaryVerticalPadding}px ${summaryHorizontalPadding}px`,
          cursor: "pointer",
          alignItems: "flex-start",
          ".MuiAccordionSummary-expandIconWrapper": {
            alignSelf: "center",
            justifySelf: "center",
            transformOrigin: "center",
          },
        }}
      >
        <Box
          display="flex"
          alignItems={"flex-start"}
          gap="0.8rem"
          margin="0"
          sx={{
            "& > svg:first-of-type": startIcon
              ? { transform: "translateY(2px)" }
              : {},
            color: colors.iconColor,
          }}
        >
          {startIcon}
          <Box display="flex" flexDirection="column" gap="0.4rem">
            <Typography variant="h6" color={colors.textColor}>
              {summaryTitle}
            </Typography>
            {summarySubtitle && (
              <Typography variant="caption" sx={{ color: "grey.600" }}>
                {summarySubtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </MuiAccordionSummary>

      {children && (
        <AccordionDetails
          sx={{
            backgroundColor: colors.backgroundColor,
            padding: `0 ${summaryHorizontalPadding}px ${detailsBottomPadding}px ${summaryHorizontalPadding}px`,
            borderRadius:
              cornerRadius === AccordionCornerRadius.small
                ? "0.8rem !important"
                : "1.6rem !important",
          }}
        >
          <div id={contentId}>{children}</div>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

/**
 * @deprecated This component is deprecated. Please use OsAccordion instead.
 */
export const AccordionSummary = React.forwardRef(
  (
    {
      // Provide default expand icon
      expandIcon = <ExpandIcon />,
      ...props
    }: AccordionSummaryProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    return <MuiAccordionSummary ref={ref} {...props} expandIcon={expandIcon} />;
  },
);
withDisplayName(AccordionSummary, "AccordionSummary");

/**
 * @deprecated This component is deprecated. Please use OsAccordion instead.
 */
export const AccordionUnstyled = styled(Accordion)({
  // Accordion root
  padding: 0,
  margin: 0,
  backgroundColor: "unset",

  [`&.${accordionClasses.disabled}`]: {
    backgroundColor: "unset",
  },
  "::before": {
    // hide top border
    opacity: 0,
  },
  [`&.${accordionClasses.expanded}`]: {
    margin: 0,
    "::before": {
      opacity: 0,
    },
    "& + &": {
      "&::before": {
        display: "unset",
        opacity: 0,
      },
    },
  },
  // Summary
  [`& .${accordionSummaryClasses.root}`]: {
    padding: 0,
    margin: 0,
    minHeight: "unset",
    backgroundColor: "unset",
    [`.${accordionSummaryClasses.content}`]: {
      padding: 0,
      margin: 0,
    },
    [`.${accordionSummaryClasses.expanded}`]: {
      padding: 0,
      margin: 0,
    },
  },
  // Details
  [`& .${accordionDetailsClasses.root}`]: {
    padding: 0,
    margin: 0,
  },
}) as typeof Accordion;
